import React from 'react';
import { PageLayoutConfig } from '@/components/layout/index';
import { LayoutElementType } from '@/components/layout/index';
import IconProvider from '@/components/ui/IconProvider';
import { LayoutElement } from '@/components/layout/layoutTypes';
import { PageRow } from '@/components/layout/page-layout/general-layout/PageLayout';
import { PageLayout } from "@/components/layout/index";
import { usePageConfig } from "@/hooks/usePageConfig";
import { useOutletContext } from "react-router-dom";
import { useAppLayoutEvents } from "@/hooks/useAppLayoutEvents";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

// Helper function to process the loaded config and convert it to the proper format
const processLoadedConfig = (config: Record<string, unknown>): PageLayoutConfig => {
  // Type assertion for the config structure
  const typedConfig = config as {
    title: string;
    spacing: number;
    padding: number;
    rows: Array<{
      id: string;
      title: string;
      layoutConfig: {
        elements: Array<{
          id: string;
          type: string;
          props: Record<string, unknown>;
        }>;
        gap: number;
        padding: number;
      };
    }>;
  };
  
  const processedConfig: PageLayoutConfig = {
    title: typedConfig.title,
    spacing: typedConfig.spacing,
    padding: typedConfig.padding,
    rows: typedConfig.rows.map((row) => ({
      id: row.id,
      title: row.title,
      layoutConfig: {
        gap: row.layoutConfig.gap,
        padding: row.layoutConfig.padding,
        elements: row.layoutConfig.elements.map((element) => {
          // Add safety check for element structure
          if (!element || typeof element !== 'object') {
            console.error('Invalid element structure:', element);
            return {
              id: 'error-element',
              type: 'stats-widget' as LayoutElementType,
              props: {}
            };
          }
          
          const processedElement = { ...element };
          
          // Ensure props exist
          if (!processedElement.props) {
            processedElement.props = {};
          }
          
          // Process icon objects to React elements
          if (processedElement.props.icon && typeof processedElement.props.icon === 'object' && processedElement.props.icon !== null) {
            const iconConfig = processedElement.props.icon as Record<string, unknown>;
            // Ensure all required properties exist with defaults
            const size = typeof iconConfig.size === 'number' ? iconConfig.size : 24;
            const color = typeof iconConfig.color === 'string' ? iconConfig.color : '#000000';
            const iconstring = typeof iconConfig.iconstring === 'string' ? iconConfig.iconstring : 'fa-file';
            
            processedElement.props = {
              ...processedElement.props,
              icon: React.createElement(IconProvider, {
                size,
                color,
                iconstring
              })
            };
          }
          
          // Process function strings to actual functions
          if (processedElement.props.onEdit && typeof processedElement.props.onEdit === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onEdit: (row: Record<string, unknown>) => {
                console.log(row);
              }
            };
          }
          
          if (processedElement.props.onDuplicate && typeof processedElement.props.onDuplicate === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onDuplicate: (row: Record<string, unknown>) => {
                console.log(row);
              }
            };
          }
          
          if (processedElement.props.onDelete && typeof processedElement.props.onDelete === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onDelete: (row: Record<string, unknown>) => {
                console.log(row);
              }
            };
          }
          
          // Process onAddNew function strings to actual functions
          if (processedElement.props.onAddNew && typeof processedElement.props.onAddNew === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onAddNew: () => {
                console.log('onAddNew called');
              }
            };
          }
          
          return {
            id: processedElement.id,
            type: processedElement.type as LayoutElementType,
            props: processedElement.props
          };
        })
      }
    }))
  };
  
  return processedConfig;
};

/**
 * Process page structure data and convert icon objects to React elements
 * @param config - Raw page structure configuration
 * @param handlers - Optional object containing handler functions for onEdit, onDuplicate, onDelete
 * @returns Processed PageLayoutConfig
 */
export const processPageStructure = (
  config: Record<string, unknown>,
  handlers?: {
    onEdit?: (row: Record<string, unknown>) => void;
    onDuplicate?: (row: Record<string, unknown>) => void;
    onDelete?: (row: Record<string, unknown>) => void;
    onAddNew?: () => void;
    onCellUpdate?: (row: Record<string, unknown>, columnKey: string, newValue: string) => void;
    onCardClick?: (card: Record<string, unknown>) => void;
  }
): PageLayoutConfig => {
  
  const typedConfig = config as {
    title: string;
    spacing: number;
    padding: number;
    rows: Array<{
      id: string;
      title: string;
      layoutConfig: {
        elements: Array<{
          id: string;
          type: string;
          props: Record<string, unknown>;
        }>;
        gap: number;
        padding: number;
      };
    }>;
  };
  
  const processedConfig: PageLayoutConfig = {
    title: typedConfig.title,
    spacing: typedConfig.spacing,
    padding: typedConfig.padding,
    rows: typedConfig.rows.map((row) => ({
      id: row.id,
      title: row.title,
      layoutConfig: {
        gap: row.layoutConfig.gap,
        padding: row.layoutConfig.padding,
        elements: row.layoutConfig.elements.map((element) => {
          // Add safety check for element structure
          if (!element || typeof element !== 'object') {
            console.error('Invalid element structure:', element);
            return {
              id: 'error-element',
              type: 'stats-widget' as LayoutElementType,
              props: {}
            };
          }
          
          const processedElement = { ...element };
          
          // Ensure props exist
          if (!processedElement.props) {
            processedElement.props = {};
          }
          
          // Move element-level properties to props (for compatibility with flat structure)
          const { id, type, props, ...elementLevelProps } = processedElement;
          Object.keys(elementLevelProps).forEach(key => {
            if (!processedElement.props[key]) {
              processedElement.props[key] = elementLevelProps[key];
            }
          });
                    
          // Process icon objects to React elements
          if (processedElement.props.icon && typeof processedElement.props.icon === 'object' && processedElement.props.icon !== null) {
            const iconConfig = processedElement.props.icon as Record<string, unknown>;
            // Ensure all required properties exist with defaults
            const size = typeof iconConfig.size === 'number' ? iconConfig.size : 24;
            const color = typeof iconConfig.color === 'string' ? iconConfig.color : '#000000';
            const iconstring = typeof iconConfig.iconstring === 'string' ? iconConfig.iconstring : 'fa-file';
            
            processedElement.props = {
              ...processedElement.props,
              icon: React.createElement(IconProvider, {
                size,
                color,
                iconstring
              })
            };
          }
          
          // Process function strings to actual functions using provided handlers
          if (processedElement.props.onEdit && typeof processedElement.props.onEdit === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onEdit: handlers?.onEdit || ((row: Record<string, unknown>) => {
                console.log('onEdit called with:', row);
              })
            };
          }
          
          if (processedElement.props.onDuplicate && typeof processedElement.props.onDuplicate === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onDuplicate: handlers?.onDuplicate || ((row: Record<string, unknown>) => {
                console.log('onDuplicate called with:', row);
              })
            };
          }
          
          if (processedElement.props.onDelete && typeof processedElement.props.onDelete === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onDelete: handlers?.onDelete || ((row: Record<string, unknown>) => {
                console.log('onDelete called with:', row);
              })
            };
          }

          // Add the missing onAddNew processing
          if (processedElement.props.onAddNew && typeof processedElement.props.onAddNew === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onAddNew: handlers?.onAddNew || (() => {
                console.log('onAddNew called');
              })
            };
          }

          // Add onCellUpdate processing for inline editing
          if (processedElement.props.onCellUpdate && typeof processedElement.props.onCellUpdate === 'string') {
            console.log('🔧 Processing onCellUpdate for element:', processedElement.id);
            processedElement.props = {
              ...processedElement.props,
              onCellUpdate: handlers?.onCellUpdate || ((row: Record<string, unknown>, columnKey: string, newValue: string) => {
                console.log('🔧 Default onCellUpdate called with:', { row, columnKey, newValue });
              })
            };
          } else if (handlers?.onCellUpdate) {
            // Always add onCellUpdate handler if available, even if not defined in element props
            console.log('🔧 Adding onCellUpdate handler to element:', processedElement.id);
            processedElement.props = {
              ...processedElement.props,
              onCellUpdate: handlers.onCellUpdate
            };
          }
          
          // Process onCardClick function strings to actual functions
          if (processedElement.props.onCardClick && typeof processedElement.props.onCardClick === 'string') {
            processedElement.props = {
              ...processedElement.props,
              onCardClick: handlers?.onCardClick || ((card: Record<string, unknown>) => {
                console.log('onCardClick called with:', card);
              })
            };
          }
          
          return {
            id: processedElement.id,
            type: processedElement.type as LayoutElementType,
            props: processedElement.props
          };
        })
      }
    }))
  };
  return processedConfig;
};

/**
 * Loads a page configuration from a JSON file in the public folder
 * @param jsonPath - Path to the JSON file relative to the public folder (e.g., 'homepagestructure.json')
 * @param fallbackConfig - Optional fallback configuration if loading fails
 * @returns Promise<PageLayoutConfig>
 */
export const loadPageConfigFromJson = async (
  jsonPath: string,
  fallbackConfig?: PageLayoutConfig
): Promise<PageLayoutConfig> => {
  try {
    const response = await fetch(`/${jsonPath}`);
    if (!response.ok) {
      throw new Error(`Failed to load page structure: ${response.statusText}`);
    }
    
    const config = await response.json();
    const processedConfig = processLoadedConfig(config);
    
    return processedConfig;
  } catch (error) {
    console.error(`Error loading page structure from ${jsonPath}:`, error);
    
    // Return fallback config if provided, otherwise return empty config
    if (fallbackConfig) {
      return fallbackConfig;
    }
    
    // Default empty config
    return {
      title: "No Page Structure Found",
      spacing: 0,
      padding: 60,
      rows: []
    };
  }
};

/**
 * Hook for loading page configuration with state management
 * @param jsonPath - Path to the JSON file relative to the public folder
 * @param fallbackConfig - Optional fallback configuration if loading fails
 * @returns Object with config, loading state, and error state
 */
export const usePageConfigLoader = (
  jsonPath: string,
  fallbackConfig?: PageLayoutConfig
) => {
  const [config, setConfig] = React.useState<PageLayoutConfig | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const loadConfig = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const loadedConfig = await loadPageConfigFromJson(jsonPath, fallbackConfig);
        setConfig(loadedConfig);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        if (fallbackConfig) {
          setConfig(fallbackConfig);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadConfig();
  }, [jsonPath, fallbackConfig]);

  return { config, isLoading, error };
}; 

/**
 * Serialize page configuration data for API storage
 * This removes React elements and functions that can't be serialized to JSON
 * @param config - The page configuration to serialize
 * @returns Serialized configuration safe for JSON storage
 */
export const serializePageConfig = (config: PageLayoutConfig): Record<string, unknown> => {
  const serializeElement = (element: LayoutElement): Record<string, unknown> => {
    const serializedProps: Record<string, unknown> = {};
    
    // Copy all props except React elements and functions
    Object.entries(element.props).forEach(([key, value]) => {
      // Skip React elements (they have a $$typeof property)
      if (value && typeof value === 'object' && '$$typeof' in value) {
        // Convert React elements to a serializable format
        if (key === 'icon' && value && typeof value === 'object' && 'props' in value) {
          // Handle icon elements specifically
          const iconProps = (value as { props: Record<string, unknown> }).props;
          serializedProps[key] = {
            size: iconProps.size || 24,
            color: iconProps.color || '#000000',
            iconstring: iconProps.iconstring || 'default'
          };
        }
        // Skip other React elements
        return;
      }
      
      // Skip functions
      if (typeof value === 'function') {
        // Convert function names to strings for storage
        if (key.startsWith('on')) {
          serializedProps[key] = value.name || key;
        }
        return;
      }
      
      // Include all other values (including booleans like showAddNew)
      serializedProps[key] = value;
    });
    
    return {
      id: element.id,
      type: element.type,
      props: serializedProps
    };
  };
  
  const serializeRow = (row: PageRow): Record<string, unknown> => ({
    id: row.id,
    title: row.title,
    layoutConfig: {
      elements: row.layoutConfig.elements.map(serializeElement),
      gap: row.layoutConfig.gap,
      padding: row.layoutConfig.padding,
      totalSlots: row.layoutConfig.totalSlots
    }
  });
  
  return {
    title: config.title,
    spacing: config.spacing,
    padding: config.padding,
    rows: config.rows.map(serializeRow)
  };
}; 